import { useState, useCallback, useEffect } from 'react'
import {
  useQuery,
  useMutation,
  useQueryClient,
  type QueryClient,
} from '@tanstack/react-query'
import {
  fetchPlanDetail,
  addPlanLine,
  updatePlanItem,
  copyPlanItem,
  deletePlanItem,
  submitPlanItems,
  bulkAddFromProductList,
  type BulkAddFromProductListResponse,
  bulkDeletePlanLines,
  moveLinesToVersion,
  createPlanVersion,
  fetchPlanVersions,
  activateVersion,
  deleteVersion,
  updatePlanVersion,
} from '@/api/planDetail'
import { queryKeys } from '@/lib/queryKeys'
import type {
  PlanDetailResponse,
  UpdatePlanItemPayload,
  ApiHierarchy,
} from '@/types'
import { useProcessingStatus } from './useProcessingStatus'
import { getEffectiveVersionId } from './useActiveVersionId'
import { useCurrentVersionStore } from '@/stores/useCurrentVersionStore'
import { toast } from '@/stores/useToastStore'
import { getErrorMessage } from '@/utils/getErrorMessage'

function invalidateDagQueries(queryClient: QueryClient, planId: string) {
  const versionId =
    useCurrentVersionStore.getState().versionsByPlan[planId] ??
    queryClient.getQueryData<string>(queryKeys.planActiveVersion.byId(planId))
  if (versionId) {
    queryClient.invalidateQueries({
      queryKey: queryKeys.dagTable.rowsPrefix(planId, versionId),
    })
    queryClient.invalidateQueries({
      queryKey: queryKeys.dagTable.metadata(planId, versionId),
    })
  }
}

export function usePlanDetail(
  planId: string | undefined,
  versionId: string | undefined,
  page: number = 1,
  pageSize: number = 10
) {
  return useQuery<PlanDetailResponse>({
    queryKey: queryKeys.planDetail.withPagination(
      planId!,
      versionId!,
      page,
      pageSize
    ),
    queryFn: () => fetchPlanDetail(planId!, versionId!, page, pageSize),
    enabled: !!planId && !!versionId,
  })
}

function extractHierarchyDefaults(hierarchy: ApiHierarchy | null | undefined) {
  if (!hierarchy) return undefined
  const all = [
    ...hierarchy.lineage,
    { name: hierarchy.name, level: hierarchy.level },
  ]
  const find = (lvl: string) => all.find(h => h.level === lvl)?.name ?? null
  return {
    division: find('DIVISION'),
    department: find('DEPARTMENT'),
    class: find('CLASS'),
    subclass: find('SUBCLASS'),
  }
}

export function useAddPlanLine(
  planId: string,
  hierarchy?: ApiHierarchy | null
) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async () => {
      const activeVersionId = await getEffectiveVersionId(queryClient, planId)
      const initialData = extractHierarchyDefaults(hierarchy)
      return addPlanLine(planId, activeVersionId, initialData)
    },
    // Cache is manually updated in the component, no need to invalidate
  })
}

export function useUpdatePlanItem(planId: string) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({
      itemId,
      updates,
    }: {
      itemId: string
      updates: UpdatePlanItemPayload
    }) => {
      const activeVersionId = await getEffectiveVersionId(queryClient, planId)
      return updatePlanItem(planId, activeVersionId, itemId, updates)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.planDetail.byId(planId),
      })
    },
  })
}

export function useCopyPlanItem(planId: string) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (itemId: string) => {
      const activeVersionId = await getEffectiveVersionId(queryClient, planId)
      return copyPlanItem(planId, activeVersionId, itemId)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.planDetail.byId(planId),
      })
      invalidateDagQueries(queryClient, planId)
    },
  })
}

export function useDeletePlanItem(planId: string) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (itemId: string) => {
      const activeVersionId = await getEffectiveVersionId(queryClient, planId)
      return deletePlanItem(planId, activeVersionId, itemId)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.planDetail.byId(planId),
      })
      invalidateDagQueries(queryClient, planId)
    },
  })
}

export function useSubmitPlanItem(planId: string) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (itemId: string) => {
      const activeVersionId = await getEffectiveVersionId(queryClient, planId)
      return submitPlanItems(planId, activeVersionId, [itemId])
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.planDetail.metadata(planId),
      })
    },
  })
}

export function useBulkImportLines(planId: string, versionId: string) {
  const queryClient = useQueryClient()
  const [processingResponse, setProcessingResponse] =
    useState<BulkAddFromProductListResponse | null>(null)

  const processing = useProcessingStatus(processingResponse)

  // Invalidate plan queries when processing completes
  useEffect(() => {
    if (processing.state === 'complete') {
      queryClient.invalidateQueries({
        queryKey: queryKeys.planDetail.byId(planId),
      })
      invalidateDagQueries(queryClient, planId)
    }
  }, [processing.state, queryClient, planId])

  const mutation = useMutation({
    mutationFn: ({
      productListId,
      upsert,
    }: {
      productListId: string
      upsert: boolean
    }) => bulkAddFromProductList(planId, versionId, productListId, upsert),
    onSuccess: data => {
      setProcessingResponse(data)
    },
  })

  const start = useCallback(
    (productListId: string, upsert = false) => {
      processing.reset()
      setProcessingResponse(null)
      mutation.mutate({ productListId, upsert })
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [mutation]
  )

  const reset = useCallback(() => {
    processing.reset()
    setProcessingResponse(null)
    mutation.reset()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const isStarting = mutation.isPending && processing.state === 'idle'

  return {
    start,
    reset,
    progress: processing.progress,
    error: processing.errorMessage ?? mutation.error?.message ?? null,
    isStarting,
    isProcessing: processing.state === 'processing',
    isCompleted: processing.state === 'complete',
    isError: processing.state === 'error' || mutation.isError,
    isPending: isStarting || processing.state === 'processing',
  }
}

export function useBulkDeleteLines(planId: string) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (lineIds: string[]) => {
      const activeVersionId = await getEffectiveVersionId(queryClient, planId)
      return bulkDeletePlanLines(planId, activeVersionId, lineIds)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.planDetail.byId(planId),
      })
      invalidateDagQueries(queryClient, planId)
    },
  })
}

export function useMoveLines(planId: string) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (variables: {
      versionId: string
      lineIds: string[]
      targetVersionId: string
      targetPlanId: string
    }) => {
      return moveLinesToVersion(
        planId,
        variables.versionId,
        variables.lineIds,
        variables.targetVersionId
      )
    },
    onSuccess: (_data, variables) => {
      // Invalidate source plan queries
      queryClient.invalidateQueries({
        queryKey: queryKeys.planDetail.byId(planId),
      })
      invalidateDagQueries(queryClient, planId)

      // Invalidate target plan queries
      queryClient.invalidateQueries({
        queryKey: queryKeys.planDetail.byId(variables.targetPlanId),
      })
      queryClient.invalidateQueries({
        queryKey: queryKeys.versions.byPlanId(variables.targetPlanId),
      })
      queryClient.invalidateQueries({
        queryKey: queryKeys.versions.byPlanId(planId),
      })
    },
  })
}

export function useBulkSubmitLines(planId: string) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (lineIds: string[]) => {
      const activeVersionId = await getEffectiveVersionId(queryClient, planId)
      return submitPlanItems(planId, activeVersionId, lineIds)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.planDetail.metadata(planId),
      })
    },
  })
}

export function usePlanVersions(planId: string) {
  return useQuery({
    queryKey: queryKeys.versions.byPlanId(planId),
    queryFn: () => fetchPlanVersions(planId),
    enabled: !!planId,
  })
}

export function useActivateVersion(planId: string) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (versionId: string) => activateVersion(planId, versionId),
    onSuccess: (_data, versionId) => {
      queryClient.setQueryData(
        queryKeys.planActiveVersion.byId(planId),
        versionId
      )
      queryClient.invalidateQueries({
        queryKey: queryKeys.versions.byPlanId(planId),
      })
      queryClient.invalidateQueries({
        queryKey: queryKeys.planDetail.byId(planId),
      })
      toast.success('Version set as active')
    },
    onError: () => {
      toast.error('Failed to activate version')
    },
  })
}

export function useUpdateVersion(planId: string) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (payload: {
      versionId: string
      alias: string
      notes: string
    }) =>
      updatePlanVersion(planId, payload.versionId, {
        alias: payload.alias,
        notes: payload.notes,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.versions.byPlanId(planId),
      })
      toast.success('Version updated successfully')
    },
    onError: error => {
      toast.error(getErrorMessage(error, 'Failed to update version'))
    },
  })
}

export function useDeleteVersion(planId: string) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (versionId: string) => deleteVersion(planId, versionId),
    onSuccess: (_data, deletedVersionId) => {
      const storedVersionId =
        useCurrentVersionStore.getState().versionsByPlan[planId]
      if (storedVersionId === deletedVersionId) {
        useCurrentVersionStore.getState().clearCurrentVersion(planId)
      }
      queryClient.invalidateQueries({
        queryKey: queryKeys.versions.byPlanId(planId),
      })
      queryClient.invalidateQueries({
        queryKey: queryKeys.planDetail.byId(planId),
      })
      toast.success('Version deleted')
    },
    onError: () => {
      toast.error('Failed to delete version')
    },
  })
}

export function useCreateVersion(planId: string) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (payload: { alias?: string; notes?: string | null }) => {
      const activeVersionId = await getEffectiveVersionId(queryClient, planId)
      return createPlanVersion(planId, {
        source_version_id: activeVersionId,
        include_lines: true,
        alias: payload.alias || undefined,
        notes: payload.notes || null,
      })
    },
    onSuccess: data => {
      useCurrentVersionStore.getState().setCurrentVersion(planId, data.id)
      queryClient.invalidateQueries({
        queryKey: queryKeys.planDetail.byId(planId),
      })
      queryClient.invalidateQueries({
        queryKey: queryKeys.versions.byPlanId(planId),
      })
      toast.success('New version saved successfully')
    },
    onError: () => {
      toast.error('Failed to save version')
    },
  })
}
